import { useRef, useState } from 'react'
import { Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { AvatarInfo, Button, InlineAlert, TextField } from '@chillout/ui'
import { AdminModal, ModalActions } from '../shared/Modal'

interface AnonymiseConfirmModalProps {
  visible: boolean
  memberName: string
  memberEmail: string
  loading: boolean
  onConfirm: () => void
  onCancel: () => void
}

export function AnonymiseConfirmModal({
  visible,
  memberName,
  memberEmail,
  loading,
  onConfirm,
  onCancel,
}: AnonymiseConfirmModalProps) {
  const { t } = useTranslation()
  const [emailInput, setEmailInput] = useState('')

  const snapshot = useRef({ memberName, memberEmail })
  if (visible) {
    snapshot.current = { memberName, memberEmail }
  }
  const s = snapshot.current

  const emailMatches = emailInput.trim().toLowerCase() === s.memberEmail.toLowerCase()

  function handleCancel() {
    setEmailInput('')
    onCancel()
  }

  return (
    <AdminModal
      visible={visible}
      onClose={handleCancel}
      closeAccessibilityLabel={t('common.cancel')}
      title={t('members.detail.anonymise.confirmTitle', { name: s.memberName })}
    >
      <View className="mt-4 gap-4">
        <AvatarInfo name={s.memberName} email={s.memberEmail} />

        <Text className="text-sm font-normal text-secondary">
          {t('members.detail.anonymise.confirmBody')}
        </Text>
        <InlineAlert message={t('members.detail.anonymise.confirmWarning')} />

        <TextField
          label={t('members.detail.anonymise.emailConfirmLabel')}
          placeholder={s.memberEmail}
          value={emailInput}
          onChangeText={setEmailInput}
          autoCapitalize="none"
          autoCorrect={false}
          error={emailInput.length > 0 && !emailMatches ? t('members.detail.anonymise.emailMismatch') : undefined}
        />
      </View>

      <View className="mt-6">
        <ModalActions>
          <Button
            label={t('common.cancel')}
            variant="secondary"
            fullWidth={false}
            onPress={handleCancel}
            disabled={loading}
          />
          <Button
            label={t('members.detail.anonymise.confirmButton')}
            loadingLabel={t('members.detail.anonymise.confirmingButton')}
            variant="primary"
            className="flex-1"
            loading={loading}
            disabled={!emailMatches}
            onPress={onConfirm}
          />
        </ModalActions>
      </View>
    </AdminModal>
  )
}